<template>
  <div :class="['fleet-assistant-root', { 'is-embedded': embedded }]">
    <div v-if="!embedded" style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Fleet Assistant</span><br/>
      <div style="font-style: italic; font-size: 18px; padding-bottom: 15px; margin-right: 10px">
        Ask questions about your fleet, navigation, or get help with the system
      </div>
    </div>

    <div class="fleet-assistant-shell">
      <!-- chat wrapper -->
      <div class="fleet-assistant-chat-wrapper">
        <!-- msgs -->
        <div ref="messagesContainer" style="flex: 1; overflow-y: auto; padding: 20px; background-color: #f9f9f9;">
          <div v-if="messages.length === 0" class="fa-welcome-empty">
            <p class="fa-welcome-title">Welcome! I'm your Fleet Assistant. How can I help you today?</p>
            <p class="fa-welcome-sub">You can ask me about:</p>
            <ul class="fa-welcome-list">
              <li>Navigation and how to use features</li>
              <li>Your current page and what you're seeing</li>
              <li>Fleet data, vehicles, drivers, and reports</li>
              <li>General questions about the system</li>
            </ul>
            <p class="fa-welcome-try">Try asking:</p>
            <div class="fa-suggested-prompts">
              <button
                v-for="prompt in suggestedPrompts"
                :key="prompt"
                type="button"
                class="fa-prompt-chip"
                :disabled="loading || uploadingDocuments"
                @click="sendSuggestedPrompt(prompt)"
              >
                {{ prompt }}
              </button>
            </div>
          </div>
          <div v-for="(message, index) in messages" :key="index" style="margin-bottom: 15px;">
            <div v-if="message.role === 'user'" style="text-align: right;">
              <div style="display: inline-block; background-color: #007ac7; color: white; padding: 10px 15px; border-radius: 18px; max-width: 70%; word-wrap: break-word;">
                {{ message.content }}
              </div>
              <div v-if="showTimestamps && message.timestamp" class="message-timestamp" style="text-align: right;">
                {{ formatTimestamp(message.timestamp) }}
              </div>
            </div>
            <div v-else style="text-align: left;">
              <div style="display: inline-block; background-color: #e9ecef; color: #333; padding-top: 10px; padding-left: 15px; padding-right: 15px; border-radius: 18px; max-width: 70%; word-wrap: break-word;" class="markdown-content" v-html="renderMarkdown(message.content)"></div>
              <div v-if="showTimestamps && message.timestamp" class="message-timestamp">
                {{ formatTimestamp(message.timestamp) }}
              </div>
              <!-- continue link for truncated responses -->
              <div v-if="message === messages[messages.length - 1] && message.is_truncated && !loading" style="margin-top: 8px; margin-left: 4px;">
                <a 
                  href="#" 
                  @click.prevent="continueResponse"
                  style="color: #007bff; text-decoration: underline; cursor: pointer; font-size: 14px;"
                  :class="{ 'text-muted': loading }"
                >
                  Continue →
                </a>
              </div>
            </div>
          </div>
          <div v-if="showThinking" style="text-align: left; margin-top: 10px;">
            <div class="thinking-indicator">
              <component :is="currentAnimationComponent" />
            </div>
          </div>
        </div>

        <!-- hidden file picker -->
        <input
          ref="faDocPicker"
          type="file"
          multiple
          accept=".pdf,.docx,.txt"
          style="display:none"
          @change="onDocPickerChange"
        />

        <!-- input -->
        <div style="border-top: 1px solid #ccc; padding: 15px; background-color: white;">
          <div v-if="sessionDocsExpanded" class="fa-session-docs">
            <div class="fa-session-docs-row">
              <b-button size="sm" variant="primary" @click="openDocPicker" :disabled="deletingDocuments">
                Add Document(s)
              </b-button>
              <b-button
                v-if="documentCount > 1"
                size="sm"
                variant="outline-danger"
                @click="deleteAllDocuments"
                :disabled="uploadingDocuments || deletingDocuments"
                title="Remove all documents"
              >
                <b-spinner v-if="deletingDocuments" small class="mr-1" />
                {{ deletingDocuments ? 'Deleting...' : 'Delete All Documents' }}
              </b-button>
              <span class="fa-session-docs-spacer"></span>
              <b-button
                v-if="isAdmin"
                size="sm"
                variant="outline-success"
                @click="clearSession"
                :disabled="loading || messages.length === 0"
                title="Clear conversation and start fresh"
              >
                Start New Chat
              </b-button>
            </div>
            <div v-if="uploadItems.length" class="fa-session-docs-list">
              <div
                v-for="item in uploadItems"
                :key="item.id"
                class="fa-attachment-pill"
                :class="'status-' + item.status"
                :title="item.error || ''"
              >
                <span class="fa-attachment-icon" aria-hidden="true">
                  <span v-if="item.status === 'failed'" class="fa-attachment-failed-icon">!</span>
                  <span v-else class="fa-attachment-file-icon" :class="'file-' + docFileType(item)">
                    <svg v-if="docFileType(item) === 'pdf'" viewBox="0 0 24 24" class="fa-attachment-logo pdf" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="8.5" font-family="Arial, sans-serif" font-weight="700" fill="#fff">PDF</text>
                    </svg>
                    <svg v-else-if="docFileType(item) === 'docx'" viewBox="0 0 24 24" class="fa-attachment-logo docx" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="10" font-family="Arial, sans-serif" font-weight="700" fill="#fff">W</text>
                    </svg>
                    <svg v-else-if="docFileType(item) === 'txt'" viewBox="0 0 24 24" class="fa-attachment-logo txt" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="8.5" font-family="Arial, sans-serif" font-weight="700" fill="#fff">TXT</text>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" class="fa-attachment-logo doc" aria-hidden="true">
                      <rect x="2" y="3" width="20" height="18" rx="3" fill="currentColor"></rect>
                      <text x="12" y="13" text-anchor="middle" dominant-baseline="middle" font-size="8.5" font-family="Arial, sans-serif" font-weight="700" fill="#fff">DOC</text>
                    </svg>
                  </span>
                </span>
                <span class="fa-attachment-name">{{ item.name }}</span>
                <span class="fa-attachment-right" aria-hidden="true">
                  <span v-if="item.status === 'queued'" class="fa-badge pending pulsing">PENDING</span>
                  <span
                    v-else-if="item.status === 'uploading'"
                    class="fa-badge upload"
                    :style="progressBadgeStyle('upload', item.progress || 0)"
                  >
                    UPLOAD {{ item.progress || 0 }}%
                  </span>
                  <span
                    v-else-if="item.status === 'processing'"
                    class="fa-badge processing pulsing"
                  >
                    LEARNING
                  </span>
                  <span v-else-if="item.status === 'failed'" class="fa-badge failed">FAILED</span>
                  <button
                    v-else
                    type="button"
                    class="fa-attachment-remove"
                    @click="removeUploadItem(item.id)"
                    :disabled="item.status === 'uploading' || deletingDocuments"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </span>
              </div>
            </div>
          </div>
          <b-form @submit.prevent="sendMessage(false)" autocomplete="off">
            <b-input-group>
              <b-input-group-prepend>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  class="fa-docs-chevron"
                  @click="sessionDocsExpanded = !sessionDocsExpanded"
                  aria-label="Toggle session documents"
                  title="Session documents"
                >
                  <svg v-if="sessionDocsExpanded" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="m7.41 8.41 4.59 4.59 4.59-4.59L18 9.83l-6 6-6-6z"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M7.41 14.59 12 10l4.59 4.59L18 13.17 12 7.17l-6 6z"/>
                  </svg>
                </b-button>
              </b-input-group-prepend>
              <b-form-input
                ref="faInput"
                v-model="currentMessage"
                placeholder="Type your message here..."
                name="fleet-assistant-input"
                autocomplete="off"
                autocapitalize="none"
                spellcheck="false"
                aria-autocomplete="none"
                style="font-size: 16px;"
                @keyup.enter="sendMessage(false)"
              ></b-form-input>
              <b-input-group-append>
                <b-button 
                  type="submit" 
                  variant="primary" 
                  :disabled="loading || uploadingDocuments || !currentMessage.trim()"
                  style="font-size: 16px; padding: 0 20px;"
                >
                  {{ loading ? 'Thinking...' : 'Send' }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { send_chat_message, get_chat_session, clear_chat_session, get_tenant_config_settings, list_fa_documents_user, presign_fa_document_user, finalize_fa_document_user, delete_fa_document_user, bulk_delete_fa_documents_user } from '../networking/apis'
import { tbn_send_chat_message, tbn_get_chat_session, tbn_clear_chat_session } from '../networking/tbn_apis'
import {
  DotsAnimation,
  SpinnerAnimation,
  VanAnimation,
  CoachBusAnimation,
  SchoolBusAnimation,
  ShuttleAnimation,
  LimoAnimation,
  TaxiAnimation,
  animationComponents
} from './animations'
import { renderMarkdownToHtml } from '../utils/markdown_renderer'

export default {
  name: 'fleet_assistant',
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DotsAnimation,
    SpinnerAnimation,
    VanAnimation,
    CoachBusAnimation,
    SchoolBusAnimation,
    ShuttleAnimation,
    LimoAnimation,
    TaxiAnimation
  },
  data() {
    return {
      messages: [],
      currentMessage: '',
      loading: false,
      showThinking: false,
      thinkingTimer: null,
      pageContext: null,
      sessionId: null,
      showTimestamps: true,
      animationType: 'dots',
      modelName: 'gpt-5.2',
      userDocs: [],
      docsLoading: false,
      sessionDocsExpanded: false,
      uploadingDocuments: false,
      deletingDocuments: false,
      uploadItems: [],
      _uploadQueue: null,
      suggestedPrompts: [
        "How's my fleet doing today?",
        "Which vehicles need attention?",
        "Show upcoming bookings",
        "Any driverless trips?",
        "Fleet status summary"
      ],
      userDocDeleting: null,
      _userDocsPollTimer: null,
      _userDocsPollDelay: 2000,
      sessionEstimatedCostCents: 0,
      sessionEstimatedCostUsd: 0,
      sessionChatCostCents: 0,
      sessionChatCostUsd: 0,
      sessionDocsCostCents: 0,
      sessionDocsCostUsd: 0
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    currentAnimationComponent() {
      return animationComponents[this.animationType] || 'DotsAnimation'
    },
    tenant_id() {
      return this.user.tenant
    },
    tenant_type() {
      return this.user.types && this.user.types[this.user.tenant] ? this.user.types[this.user.tenant] : 'CM'
    },
    enabled_feature_flags() {
      const featureMap = this.user && this.user.features && this.user.features[this.user.tenant]
        ? this.user.features[this.user.tenant]
        : {}
      return Object.keys(featureMap).filter(flag => featureMap[flag])
    },
    documentCount() {
      return (this.uploadItems || []).length
    },
    is_tbn() {
      return this.tenant_type === 'TBN'
    },
    isAdmin() {
      return this.user.role === 'admin'
    },
    messageEstimatedCostDollars() {
      return (this.messages || []).reduce((sum, msg) => {
        if (!msg || msg.role !== 'assistant' || !msg.usage) return sum
        return sum + this.getUsageCostDollars(msg.usage)
      }, 0)
    },
    displaySessionCostDollars() {
      if (Number.isFinite(this.sessionEstimatedCostUsd) && this.sessionEstimatedCostUsd > 0) {
        return this.sessionEstimatedCostUsd
      }
      if (Number.isFinite(this.sessionEstimatedCostCents) && this.sessionEstimatedCostCents > 0) {
        return this.parseCostCents(this.sessionEstimatedCostCents, 0) / 100
      }
      return this.messageEstimatedCostDollars
    },
    sessionChatCostDollars() {
      if (Number.isFinite(this.sessionChatCostUsd) && this.sessionChatCostUsd > 0) {
        return this.sessionChatCostUsd
      }
      if (Number.isFinite(this.sessionChatCostCents) && this.sessionChatCostCents > 0) {
        return this.parseCostCents(this.sessionChatCostCents, 0) / 100
      }
      return this.messageEstimatedCostDollars
    },
    sessionDocsCostDollars() {
      if (Number.isFinite(this.sessionDocsCostUsd) && this.sessionDocsCostUsd > 0) {
        return this.sessionDocsCostUsd
      }
      if (Number.isFinite(this.sessionDocsCostCents) && this.sessionDocsCostCents > 0) {
        return this.parseCostCents(this.sessionDocsCostCents, 0) / 100
      }
      return Math.max(0, this.displaySessionCostDollars - this.sessionChatCostDollars)
    },
    lastAssistantCostDollars() {
      for (let idx = this.messages.length - 1; idx >= 0; idx--) {
        const msg = this.messages[idx]
        if (!msg || msg.role !== 'assistant' || !msg.usage) continue
        const value = this.getUsageCostDollars(msg.usage)
        if (value > 0) {
          return value
        }
      }
      return 0
    },
  },
  watch: {
    '$route.fullPath'() {
      this.gatherPageContext()
    },
    tenant_id(newTenantId, oldTenantId) {
      // tenant switched - need fresh chat
      if (oldTenantId && newTenantId !== oldTenantId) {
        console.log('Tenant changed from', oldTenantId, 'to', newTenantId, '- reloading chat session')
        this.messages = []
        this.sessionEstimatedCostCents = 0
        this.sessionEstimatedCostUsd = 0
        this.sessionChatCostCents = 0
        this.sessionChatCostUsd = 0
        this.sessionDocsCostCents = 0
        this.sessionDocsCostUsd = 0
        this.loadPreviousMessages()
      }
    }
  },
  mounted() {
    this.gatherPageContext()
    this.loadPreviousMessages()
    this.loadTenantSettings()
    this.load_user_docs()
    this._onLinkClick = this.handleRenderedLinkClick.bind(this)
    if (this.$refs.messagesContainer) {
      this.$refs.messagesContainer.addEventListener('click', this._onLinkClick)
    }
    this.$root.$on('fa-settings-changed', this.loadTenantSettings)
    this.$root.$on('fa-docs-changed', this.load_user_docs)
  },
  beforeDestroy() {
    this.stopUserDocsRefresh()
    if (this.$refs.messagesContainer && this._onLinkClick) {
      this.$refs.messagesContainer.removeEventListener('click', this._onLinkClick)
    }
    this.$root.$off('fa-settings-changed', this.loadTenantSettings)
    this.$root.$off('fa-docs-changed', this.load_user_docs)
  },
  methods: {
    async removeUploadItem(id) {
      const item = (this.uploadItems || []).find(x => x.id === id)
      if (!item) return
      if (item.status === 'uploading' || item.status === 'processing') return

      // If already uploaded, delete from the user's document library too
      if (item.status === 'uploaded' && item.doc_id) {
        try {
          await delete_fa_document_user(this.tenant_id, item.doc_id)
          await this.load_user_docs()
        } catch (e) {
          const msg = e && e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed'
          this.$emit('showAlert', msg, 'danger', 5000)
          return
        }
      }

      this.uploadItems = (this.uploadItems || []).filter(x => x.id !== id)
    },
    async deleteAllDocuments() {
      const items = (this.uploadItems || []).slice()
      const docIds = items.filter(x => x.doc_id && (x.status === 'uploaded' || x.status === 'failed')).map(x => x.doc_id)
      this.deletingDocuments = true
      try {
        if (docIds.length) {
          await bulk_delete_fa_documents_user(this.tenant_id, docIds)
        }
        this.uploadItems = []
        await this.load_user_docs()
      } catch (e) {
        var msg = e && e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed'
        this.$emit('showAlert', msg, 'danger', 5000)
      } finally {
        this.deletingDocuments = false
      }
    },
    handleRenderedLinkClick(event) {
      var anchor = event.target.closest('a[data-spa-nav], a[data-help-article]')
      if (!anchor) return
      event.preventDefault()
      var href = anchor.getAttribute('href')
      if (href && this.$router) {
        this.$router.push(href).catch(function () {})
      }
    },
    sendSuggestedPrompt(prompt) {
      this.currentMessage = prompt
      this.$nextTick(() => this.sendMessage(false))
    },
    openDocPicker() {
      if (this.$refs.faDocPicker) this.$refs.faDocPicker.click()
    },
    reloadSettings() {
      this.loadTenantSettings()
      this.load_user_docs()
    },
    focusInput() {
      this.$nextTick(() => {
        const ref = this.$refs.faInput
        if (!ref) return
        const el = ref.$el || ref
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    onDocPickerChange(e) {
      const files = e && e.target && e.target.files ? Array.from(e.target.files) : []
      // reset so selecting same file again triggers change
      if (e && e.target) e.target.value = null
      if (!files.length) return
      this.enqueueUploads(files)
    },
    enqueueUploads(files) {
      if (!files || !files.length) return
      const nowSec = Math.floor(Date.now() / 1000)
      const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB
      const MAX_DOCS = 25
      // count server-side docs + in-flight uploads (deduplicating by doc_id)
      const serverDocIds = new Set((this.userDocs || []).map(d => d.doc_id).filter(Boolean))
      const inFlightCount = (this.uploadItems || []).filter(x =>
        x.status !== 'failed' && !(x.doc_id && serverDocIds.has(x.doc_id))
      ).length
      const currentCount = serverDocIds.size + inFlightCount

      const newItems = []
      for (let idx = 0; idx < files.length; idx++) {
        const f = files[idx]
        const item = {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}-${idx}`,
          name: f.name,
          file: f,
          status: 'queued',
          error: null,
          progress: 0,
          uploaded_at: nowSec,
        }
        if (f.size > MAX_FILE_SIZE) {
          item.status = 'failed'
          item.error = `${f.name} is ${(f.size / (1024 * 1024)).toFixed(1)} MB — maximum is 25 MB`
          item.file = null
        } else if (currentCount + newItems.filter(x => x.status === 'queued').length >= MAX_DOCS) {
          item.status = 'failed'
          item.error = `Document limit reached (${MAX_DOCS}). Delete a document to add another.`
          item.file = null
        }
        newItems.push(item)
      }
      this.uploadItems = [...(this.uploadItems || []), ...newItems]

      if (!this._uploadQueue) this._uploadQueue = Promise.resolve()
      this._uploadQueue = this._uploadQueue
        .then(() => this._processUploadQueue())
        .catch(() => this._processUploadQueue())
    },
    async _processUploadQueue() {
      // prevent re-entrancy
      if (this.uploadingDocuments) return

      const queued = (this.uploadItems || []).filter(x => x.status === 'queued')
      if (!queued.length) return

      this.uploadingDocuments = true
      const CONCURRENCY = 3
      const finalizePromises = []
      let index = 0

      // each worker pulls the next queued item, uploads it to S3, fires off
      // finalize in the background, then immediately grabs the next item.
      const worker = async () => {
        while (index < queued.length) {
          const item = queued[index++]
          await this._uploadItem(item, finalizePromises)
        }
      }

      try {
        // spawn up to CONCURRENCY workers draining the queue in parallel
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, queued.length) }, () => worker())
        )
        // all S3 uploads done — wait for trailing finalize calls to settle
        await Promise.allSettled(finalizePromises)
        await this.load_user_docs()
      } finally {
        this.uploadingDocuments = false
      }
    },
    // handles presign + S3 upload, then kicks off finalize without blocking
    async _uploadItem(item, finalizePromises) {
      this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, status: 'uploading', error: null, progress: 0 } : x)
      try {
        const file = item.file
        const filename = file && file.name ? file.name : 'document'
        const contentType = file && file.type ? file.type : 'application/octet-stream'
        const presignResp = await presign_fa_document_user(this.tenant_id, {
          filename,
          content_type: contentType,
          file_size: file && file.size ? file.size : 0,
          display_name: filename,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data ? (presignResp.value.data.data || presignResp.value.data) : {}
        const upload = presignData.upload || {}
        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)
        await this.uploadToS3WithProgress(upload.url, uploadForm, (pct) => {
          this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, progress: pct } : x)
        })
        // S3 upload done — mark processing and set doc_id so the merge logic
        // in load_user_docs can match this item against the server version.
        this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, status: 'processing', error: null, progress: 0, doc_id: presignData.doc_id } : x)
        finalizePromises.push(this._finalizeItem(item.id, presignData, filename, contentType))
        // worker returns immediately to pick up the next queued item
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Upload failed'
        console.warn('[FA Docs] Upload error', err)
        this.uploadItems = (this.uploadItems || []).map(x => x.id === item.id ? { ...x, status: 'failed', error: msg, progress: 0 } : x)
      }
    },
    async _finalizeItem(itemId, presignData, filename, contentType) {
      try {
        const finalizeResp = await finalize_fa_document_user(this.tenant_id, {
          doc_id: presignData.doc_id,
          filename,
          content_type: contentType,
          display_name: presignData.display_name || filename,
        })
        const payload = finalizeResp && finalizeResp.value && finalizeResp.value.data ? (finalizeResp.value.data.data || finalizeResp.value.data) : {}
        const docId = payload && payload.doc_id ? payload.doc_id : null
        this.applySessionCostFromPayload(payload)
        this.uploadItems = (this.uploadItems || []).map(x => x.id === itemId ? { ...x, status: 'uploaded', error: null, doc_id: docId, file: null, progress: 100 } : x)
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Processing failed'
        this.uploadItems = (this.uploadItems || []).map(x => x.id === itemId ? { ...x, status: 'failed', error: msg, progress: 0 } : x)
      }
    },
    gatherPageContext() {
      var navbar = document.getElementById('navbar')
      var faLauncher = document.querySelector('.fa-widget-root')
      var isChrome = function (node) {
        if (!node) return false
        if (navbar && navbar.contains(node)) return true
        if (faLauncher && faLauncher.contains(node)) return true
        return false
      }
      var isInsideAssistant = function (node) {
        return this.$el && node && this.$el.contains(node)
      }.bind(this)
      var compactText = function (text) { return (text || '').replace(/\s+/g, ' ').trim() }

      var context = {
        page_title: document.title,
        route_name: this.$route.name,
        route_path: this.$route.path,
        route_query: this.$route.query || {},
        route_params: this.$route.params || {},
        url: window.location.href,
        timestamp: new Date().toISOString()
      }

      var headingTexts = Array.from(document.querySelectorAll('h1, h2, h3'))
        .filter(function (h) { return !isInsideAssistant(h) && !isChrome(h) })
        .map(function (h) { return compactText(h.textContent) })
        .filter(Boolean)
        .slice(0, 5)

      var textNodes = Array.from(document.querySelectorAll('h1, h2, h3, h4, p, td, th, label, button, a, span, div.col, div.text-center'))
      var seen = {}
      var visibleText = []
      var visibleTextLength = 0
      for (var idx = 0; idx < textNodes.length; idx++) {
        var node = textNodes[idx]
        if (isInsideAssistant(node) || isChrome(node)) continue
        var value = compactText(node.innerText || node.textContent)
        if (!value || value.length < 3) continue
        if (seen[value]) continue
        seen[value] = true
        visibleText.push(value)
        visibleTextLength += value.length + 1
        if (visibleTextLength >= 1500) break
      }

      this.pageContext = {
        ...context,
        headings: headingTexts,
        visible_text: visibleText.join(' ').slice(0, 1500)
      }
    },
    async sendMessage(continueResponse = false) {
      // empty msg ok if we're just continuing
      if (!continueResponse && (!this.currentMessage.trim() || this.loading || this.uploadingDocuments)) {
        return
      }

      // refresh context just before request so route/page changes are reflected
      this.gatherPageContext()

      const userMessage = continueResponse ? 'continue' : this.currentMessage.trim()
      if (!continueResponse) {
        this.currentMessage = ''
      }
      
      // only show user msg bubble for actual messages, not "continue"
      if (!continueResponse) {
        this.messages.push({
          role: 'user',
          content: userMessage,
          timestamp: new Date().toISOString()
        })
      }
      
      // clear old truncation marker when continuing
      if (continueResponse && this.messages.length > 0 && this.messages[this.messages.length - 1].role === 'assistant') {
        this.messages[this.messages.length - 1].is_truncated = false
      }

      this.$nextTick(() => {
        this.scrollToBottom()
      })

      this.loading = true
      this.thinkingTimer = setTimeout(() => {
        this.showThinking = true
        this.$nextTick(() => { this.scrollToBottom() })
      }, 1000)

      try {
        const userDocIds = (this.uploadItems || [])
          .filter(item => item && item.doc_id && item.status === 'uploaded')
          .map(item => item.doc_id)
        const fleetContext = {
          page_context: this.pageContext,
          user_role: this.user.role,
          tenant_name: this.user.tenant_name || this.tenant_id,
          current_page: this.$route.name,
          tenant_type: this.tenant_type,
          enabled_features: this.enabled_feature_flags
        }

        const payload = {
          message: userMessage,
          fleet_context: fleetContext,
          tenant: this.tenant_id,
          continue_response: continueResponse,
          include_company_docs: true,
          include_user_docs: userDocIds.length > 0,
          user_doc_ids: userDocIds,
          company_doc_ids: []
        }

        console.log('FleetAssistant: Sending message', { 
          userMessage: userMessage.substring(0, 50), 
          continueResponse, 
          messageLength: userMessage.length,
          is_tbn: this.is_tbn
        })

        // use TBN API for TBN tenants, regular API for others
        const chatFunction = this.is_tbn ? tbn_send_chat_message : send_chat_message
        const { value: { data: responseData = {} } } = await chatFunction(this.tenant_id, payload)
        
        const data = responseData.data || responseData // unwrap the ApiSuccessResponse
        this.applySessionCostFromPayload(data)

        const reply = data.reply || 'Sorry, I could not generate a response. Please try again.'
        const isTruncated = data.is_truncated || false
        const continuationUsage = this.extractLatestAssistantUsage(data.previous_messages)
        
        // store session id for admin display
        if (data.session_id) {
          this.sessionId = data.session_id
        }
        
        // append to existing msg if continuing, otherwise add new bubble
        if (continueResponse && this.messages.length > 0 && this.messages[this.messages.length - 1].role === 'assistant') {
          this.messages[this.messages.length - 1].content = this.messages[this.messages.length - 1].content + "\n\n" + reply
          this.messages[this.messages.length - 1].is_truncated = isTruncated
          if (continuationUsage) {
            this.messages[this.messages.length - 1].usage = this.mergeUsage(
              this.messages[this.messages.length - 1].usage,
              continuationUsage
            )
          }
        } else {
          // server returns last 10 turns - just use those
          if (data.previous_messages && Array.isArray(data.previous_messages) && data.previous_messages.length > 0) {
            this.messages = data.previous_messages.map((msg, idx) => {
              // add timestamp if not present
              const timestamp = msg.timestamp || new Date().toISOString()
              if (msg.role === 'assistant' && idx === data.previous_messages.length - 1) {
                return { ...msg, is_truncated: isTruncated, timestamp }
              }
              return { ...msg, timestamp }
            })
            console.log('Updated messages from server:', this.messages.length)
          } else {
            // fallback - shouldnt really happen
            console.log('Chat response received:', { session_id: data.session_id, reply_length: reply.length, is_truncated: isTruncated })
            this.messages.push({
              role: 'assistant',
              content: reply,
              is_truncated: isTruncated,
              timestamp: new Date().toISOString()
            })
          }
        }
        
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (error) {
        console.error('Error sending message:', error)
        const msg = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Error sending message. Please try again or contact support if the issue persists.'
        this.$emit('showAlert', msg, 'danger', 5000)
        
        this.messages.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again or rephrase your question.',
          timestamp: new Date().toISOString()
        })
      } finally {
        clearTimeout(this.thinkingTimer)
        this.showThinking = false
        this.loading = false
        this.uploadingDocuments = false
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    // (legacy) onFilesSelected removed — uploads now start from file dialog
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },
    continueResponse() {
      this.sendMessage(true) // pick up where we left off
    },
    renderMarkdown(text) {
      return renderMarkdownToHtml(text, {
        stripAssistantClosings: true,
        addLastElementClass: true,
      })
    },
    async loadPreviousMessages() {
      try {
        this.loading = true
        console.log('Loading previous messages from server-side session', { is_tbn: this.is_tbn })
        
        // use TBN API for TBN tenants, regular API for others
        const sessionFunction = this.is_tbn ? tbn_get_chat_session : get_chat_session
        const { value: { data: responseData = {} } } = await sessionFunction(this.tenant_id)
        const data = responseData.data || responseData
        this.applySessionCostFromPayload(data)
        
        // store session id for admin display
        if (data.session_id) {
          this.sessionId = data.session_id
        }
        
        if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          // ensure messages have timestamps
          this.messages = data.messages.map(msg => ({
            ...msg,
            timestamp: msg.timestamp || null
          }))
          console.log('Loaded previous messages:', this.messages.length)
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      } catch (error) {
        console.error('Error loading previous messages:', error)
        // no session yet = empty chat, thats fine
      } finally {
        this.loading = false
      }
    },
    async loadTenantSettings() {
      try {
        const { value: { data = {} } } = await get_tenant_config_settings(this.tenant_id)
        const settings = data.results || {}
        if (settings.fleet_assistant_animation) {
          this.animationType = settings.fleet_assistant_animation
        }
        if (settings.openai_model) {
          this.modelName = settings.openai_model
        }
      } catch (error) {
        console.error('Error loading tenant settings:', error)
        // use defaults
      }
    },
    async load_user_docs() {
      this.docsLoading = true
      try {
        const { value: { data = {} } } = await list_fa_documents_user(this.tenant_id)
        const results = data.data || data
        this.userDocs = results.documents || []

        // merge server docs into uploadItems (preserve any in-progress uploads)
        const serverDocs = this.userDocs.map(doc => ({
          id: doc.doc_id,
          name: doc.display_name || doc.doc_id,
          file: null,
          status: doc.status || 'uploaded',
          error: null,
          doc_id: doc.doc_id,
          doc_type: doc.doc_type,
          content_type: doc.content_type,
          uploaded_at: doc.uploaded_at || 0,
        }))
        // build server status lookup so we can detect when the server has
        // moved past 'processing'-- in that case the server version wins.
        const serverStatusMap = new Map(serverDocs.filter(d => d.doc_id).map(d => [d.doc_id, d.status]))
        const inProgress = (this.uploadItems || []).filter(x => {
          if (x.status === 'queued' || x.status === 'uploading') return true
          if (x.status === 'processing') {
            if (!x.doc_id) return true // presign hasn't returned yet
            const srvStatus = serverStatusMap.get(x.doc_id)
            // Keep local version only if server doesn't know about it yet
            // or server also says 'processing'; otherwise let server win.
            return !srvStatus || srvStatus === 'processing'
          }
          return false
        })
        // combine: in-progress first, then server docs (excluding duplicates)
        const inProgressIds = new Set(inProgress.map(x => x.doc_id).filter(Boolean))
        const merged = [...inProgress, ...serverDocs.filter(d => !inProgressIds.has(d.doc_id))]
        this.uploadItems = this.sortByUploadedAt(merged)
        this.scheduleUserDocsRefresh()
      } catch (e) {
        console.warn('Failed to load user documents:', e)
        this.scheduleUserDocsRefresh()
      } finally {
        this.docsLoading = false
      }
    },
    async delete_user_doc(docId) {
      if (!confirm('Delete this document?')) return
      this.userDocDeleting = docId
      try {
        await delete_fa_document_user(this.tenant_id, docId)
        await this.load_user_docs()
        this.$emit('showAlert', 'Document deleted', 'success', 3000)
      } catch (e) {
        this.$emit('showAlert', e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Delete failed', 'danger', 5000)
      } finally {
        this.userDocDeleting = null
      }
    },
    uploadToS3WithProgress(url, formData, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url, true)
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            // S3 returns XML error bodies — try to extract a useful message
            let detail = `S3 upload failed (HTTP ${xhr.status})`
            try {
              const xml = xhr.responseText || ''
              const codeMatch = xml.match(/<Code>([^<]+)<\/Code>/)
              const msgMatch = xml.match(/<Message>([^<]+)<\/Message>/)
              if (codeMatch || msgMatch) {
                detail = [codeMatch && codeMatch[1], msgMatch && msgMatch[1]].filter(Boolean).join(': ')
              }
            } catch (_) { /* ignore parse errors */ }
            reject(new Error(detail))
          }
        }
        xhr.onerror = () => reject(new Error('S3 upload failed (network error)'))
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable || typeof onProgress !== 'function') return
          const pct = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
          onProgress(pct)
        }
        xhr.send(formData)
      })
    },
    progressBadgeStyle(kind, pct) {
      const clamped = Math.max(0, Math.min(100, Math.round(pct || 0)))
      const color = kind === 'processing' ? '#1e7eeb' : '#28a745'
      const bg = '#e9ecef'
      return {
        '--progress': `${clamped}%`,
        '--badge-color': color,
        '--badge-bg': bg
      }
    },
    sortByUploadedAt(items) {
      return (items || []).slice().sort((a, b) => {
        const aTs = Number(a && a.uploaded_at) || 0
        const bTs = Number(b && b.uploaded_at) || 0
        return bTs - aTs
      })
    },
    scheduleUserDocsRefresh() {
      const serverProcessing = (this.userDocs || []).some(d => (d.status || 'uploaded') === 'processing')
      const localProcessing = (this.uploadItems || []).some(x => x.status === 'processing')
      if (!serverProcessing && !localProcessing) {
        this.stopUserDocsRefresh()
        return
      }
      if (this._userDocsPollTimer) return
      const delay = this._userDocsPollDelay || 2000
      this._userDocsPollTimer = setTimeout(async () => {
        this._userDocsPollTimer = null
        if (this.docsLoading) {
          this.scheduleUserDocsRefresh()
          return
        }
        await this.load_user_docs()
        this._userDocsPollDelay = Math.min(Math.round((this._userDocsPollDelay || 2000) * 1.5), 15000)
      }, delay)
    },
    stopUserDocsRefresh() {
      if (this._userDocsPollTimer) {
        clearTimeout(this._userDocsPollTimer)
        this._userDocsPollTimer = null
      }
      this._userDocsPollDelay = 2000
    },
    docFileType(item) {
      const name = (item && (item.name || (item.file && item.file.name))) || ''
      const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
      if (ext === 'pdf') return 'pdf'
      if (ext === 'docx' || ext === 'doc') return 'docx'
      if (ext === 'txt') return 'txt'
      const ct = (item && item.content_type) || ''
      if (ct.includes('pdf')) return 'pdf'
      if (ct.includes('word')) return 'docx'
      if (ct.includes('text')) return 'txt'
      return 'doc'
    },
    async clearSession() {
      if (!confirm('Clear conversation and start fresh? This cannot be undone.')) {
        return
      }
      
      try {
        this.loading = true
        const clearFunction = this.is_tbn ? tbn_clear_chat_session : clear_chat_session
        await clearFunction(this.tenant_id)
        this.messages = []
        this.sessionId = null
        this.sessionEstimatedCostCents = 0
        this.sessionEstimatedCostUsd = 0
        this.sessionChatCostCents = 0
        this.sessionChatCostUsd = 0
        this.sessionDocsCostCents = 0
        this.sessionDocsCostUsd = 0
        console.log('Session cleared successfully')
      } catch (error) {
        console.error('Error clearing session:', error)
        this.$emit('showAlert', 'Error clearing session. Please try again.', 'danger', 3000)
      } finally {
        this.loading = false
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      try {
        const date = new Date(timestamp)
        return date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return ''
      }
    },
    formatCostDollars(dollars) {
      const normalized = this.parseCostDollars(dollars, 0)
      if (normalized <= 0) return '$0.0000'
      if (normalized < 0.0001) return '<$0.0001'
      const decimals = normalized < 1 ? 4 : 2
      return `$${normalized.toFixed(decimals)}`
    },
    parseCostCents(value, fallback = 0) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed < 0) return Math.max(0, Math.round(fallback || 0))
      return Math.round(parsed)
    },
    parseCostDollars(value, fallback = 0) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed < 0) {
        const fallbackNum = Number(fallback)
        return Number.isFinite(fallbackNum) ? fallbackNum : 0
      }
      return Math.max(0, parsed)
    },
    getUsageCostDollars(usage) {
      if (!usage || typeof usage !== 'object') return 0
      if (usage.estimated_cost_usd !== undefined && usage.estimated_cost_usd !== null) {
        return this.parseCostDollars(usage.estimated_cost_usd, 0)
      }
      return this.parseCostCents(usage.estimated_cost_cents, 0) / 100
    },
    applySessionCostFromPayload(payload) {
      if (!payload || typeof payload !== 'object') return
      if (payload.session_estimated_cost_cents !== undefined) {
        this.sessionEstimatedCostCents = this.parseCostCents(
          payload.session_estimated_cost_cents,
          this.sessionEstimatedCostCents
        )
      }
      if (payload.session_estimated_cost_usd !== undefined) {
        this.sessionEstimatedCostUsd = this.parseCostDollars(
          payload.session_estimated_cost_usd,
          this.sessionEstimatedCostUsd
        )
      }
      if (payload.session_chat_estimated_cost_cents !== undefined) {
        this.sessionChatCostCents = this.parseCostCents(
          payload.session_chat_estimated_cost_cents,
          this.sessionChatCostCents
        )
      }
      if (payload.session_chat_estimated_cost_usd !== undefined) {
        this.sessionChatCostUsd = this.parseCostDollars(
          payload.session_chat_estimated_cost_usd,
          this.sessionChatCostUsd
        )
      }
      if (payload.session_docs_estimated_cost_cents !== undefined) {
        this.sessionDocsCostCents = this.parseCostCents(
          payload.session_docs_estimated_cost_cents,
          this.sessionDocsCostCents
        )
      }
      if (payload.session_docs_estimated_cost_usd !== undefined) {
        this.sessionDocsCostUsd = this.parseCostDollars(
          payload.session_docs_estimated_cost_usd,
          this.sessionDocsCostUsd
        )
      }
    },
    normalizeUsage(rawUsage) {
      if (!rawUsage || typeof rawUsage !== 'object') return null
      return {
        input_tokens: this.parseCostCents(rawUsage.input_tokens, 0),
        output_tokens: this.parseCostCents(rawUsage.output_tokens, 0),
        estimated_cost_cents: this.parseCostCents(rawUsage.estimated_cost_cents, 0),
        estimated_cost_usd: this.parseCostDollars(
          rawUsage.estimated_cost_usd,
          this.parseCostCents(rawUsage.estimated_cost_cents, 0) / 100
        ),
        api_calls: this.parseCostCents(rawUsage.api_calls, 0),
        model: rawUsage.model || null
      }
    },
    mergeUsage(existingUsage, additionalUsage) {
      const current = this.normalizeUsage(existingUsage)
      const next = this.normalizeUsage(additionalUsage)
      if (!current) return next
      if (!next) return current
      return {
        input_tokens: current.input_tokens + next.input_tokens,
        output_tokens: current.output_tokens + next.output_tokens,
        estimated_cost_cents: current.estimated_cost_cents + next.estimated_cost_cents,
        estimated_cost_usd: current.estimated_cost_usd + next.estimated_cost_usd,
        api_calls: current.api_calls + next.api_calls,
        model: next.model || current.model || null
      }
    },
    extractLatestAssistantUsage(previousMessages) {
      if (!Array.isArray(previousMessages) || !previousMessages.length) return null
      for (let idx = previousMessages.length - 1; idx >= 0; idx--) {
        const msg = previousMessages[idx]
        if (msg && msg.role === 'assistant' && msg.usage) {
          return this.normalizeUsage(msg.usage)
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
.fleet-assistant-root {
  margin-top: 20px;
}

.fleet-assistant-shell {
  max-width: 1200px;
  margin: 0 auto;
}

.fleet-assistant-chat-wrapper {
  border: 1px solid #ccc;
  border-radius: 8px;
  height: 600px;
  display: flex;
  flex-direction: column;
  background-color: white;
  overflow: hidden;
}

.fleet-assistant-root.is-embedded {
  margin-top: 0;
  height: 100%;
}

.fleet-assistant-root.is-embedded .fleet-assistant-shell {
  max-width: none;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.fleet-assistant-root.is-embedded .fleet-assistant-chat-wrapper {
  height: auto;
  flex: 1;
  min-height: 0;
  border: none;
  border-radius: 0;
}

.fa-welcome-empty {
  text-align: center;
  color: #666;
  margin-top: 50px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}

.fa-welcome-title {
  font-size: 18px;
  margin: 0;
}

.fa-welcome-sub {
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 0;
}

.fa-welcome-list {
  text-align: left;
  display: inline-block;
  margin-top: 10px;
  padding-left: 20px;
}

.fa-welcome-try {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-top: 16px;
  margin-bottom: 8px;
}

.fa-suggested-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.fa-prompt-chip {
  font-size: 14px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(0, 122, 199, 0.35);
  background: rgba(0, 122, 199, 0.08);
  color: #007ac7;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.fa-prompt-chip:hover:not(:disabled) {
  background: rgba(0, 122, 199, 0.14);
  border-color: rgba(0, 122, 199, 0.5);
}

.fa-prompt-chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fa-session-docs {
  margin-bottom: 10px;
}

.fa-session-docs-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.fa-session-docs-spacer {
  flex: 1;
  min-width: 8px;
}

.fa-session-docs-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
}

.fa-docs-chevron {
  min-width: 40px;
  padding-left: 6px;
  padding-right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fa-attachment-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  padding: 6px 10px;
  max-width: 100%;
}

.fa-attachment-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #666;
}

.fa-attachment-failed-icon {
  font-weight: 800;
  color: #c82333;
}

.fa-attachment-queued-icon {
  color: #999;
}

.fa-attachment-file-icon {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.fa-attachment-logo {
  width: 20px;
  height: 20px;
  display: inline-block;
}

.fa-attachment-logo.pdf {
  color: #c62828;
}

.fa-attachment-logo.docx {
  color: #1565c0;
}

.fa-attachment-logo.txt {
  color: #455a64;
}

.fa-attachment-logo.doc {
  color: #6c757d;
}

.fa-attachment-name {
  font-size: 12px;
  color: #333;
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fa-attachment-right {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.fa-badge {
  --progress: 0%;
  --badge-color: #6c757d;
  --badge-bg: #e9ecef;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  min-height: 20px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--badge-color);
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.08) var(--progress), var(--badge-bg) var(--progress));
  color: #333;
  white-space: nowrap;
}

.fa-badge.upload {
  --badge-color: #28a745;
  --badge-bg: #e9f6ee;
  color: #155724;
  background: linear-gradient(90deg, rgba(40, 167, 69, 0.25) var(--progress), var(--badge-bg) var(--progress));
}

.fa-badge.processing {
  --badge-color: #1e7eeb;
  --badge-bg: #e7f1ff;
  color: #0b3d91;
  background: var(--badge-bg);
}

.fa-badge.pending {
  --badge-color: #fd7e14;
  --badge-bg: #fff4e5;
  --pulse-color: rgba(253, 126, 20, 0.35);
  color: #7a3e00;
  background: var(--badge-bg);
}

.fa-badge.failed {
  --badge-color: #b00020;
  --badge-bg: #fde8ea;
  color: #b00020;
}

.fa-badge.processing.pulsing {
  --pulse-color: rgba(30, 126, 235, 0.35);
}

.fa-badge.pulsing {
  animation: faBadgePulse 1.2s ease-in-out infinite;
}

@keyframes faBadgePulse {
  0% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
  50% {
    box-shadow: inset 0 0 6px 3px var(--pulse-color);
    opacity: 1;
  }
  100% {
    box-shadow: inset 0 0 0 0 var(--pulse-color);
    opacity: 0.85;
  }
}

.fa-attachment-pill.status-uploaded {
  border-color: steelblue;
  background: #fff;
}

.fa-attachment-pill.status-uploaded .fa-attachment-icon {
  color: steelblue;
}

.fa-attachment-pill.status-failed {
  border-color: #dc3545;
  background: #fff;
}

.fa-attachment-pill.status-uploading {
  border-color: #28a745;
  background: #fff;
}

.fa-attachment-pill.status-processing {
  border-color: #1e7eeb;
  background: #fff;
}

.fa-attachment-remove {
  border: none;
  background: transparent;
  padding: 0 4px;
  font-size: 16px;
  line-height: 1;
  color: #777;
}

.fa-attachment-remove:disabled {
  opacity: 0.4;
}


.message-timestamp {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  padding: 0 4px;
}

.thinking-indicator {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  color: #333;
  padding: 10px 15px;
  border-radius: 18px;
  gap: 8px;
}

.markdown-content {
  text-align: left;
}

.markdown-content strong {
  font-weight: bold;
}

.markdown-content em {
  font-style: italic;
}

.markdown-content ol,
.markdown-content ul {
  margin: 8px 0;
  padding-left: 20px;
}

.markdown-content li {
  margin: 4px 0;
  line-height: 1.4;
}

.markdown-content ol {
  list-style-type: decimal;
}

.markdown-content ul {
  list-style-type: disc;
}

/* nested list styling */
.markdown-content ul ul,
.markdown-content ol ul {
  margin: 4px 0;
  padding-left: 18px;
  list-style-type: circle;
}

.markdown-content ul ul ul,
.markdown-content ol ul ul {
  list-style-type: square;
}

.markdown-content ul ol,
.markdown-content ol ol {
  margin: 4px 0;
  padding-left: 18px;
}

.markdown-content br {
  line-height: 1.6;
}

.markdown-content .md-link {
  color: #0068a8;
  text-decoration: none;
  cursor: pointer;
  word-break: break-word;
}

.markdown-content .md-link:hover {
  text-decoration: underline;
  color: #004a7c;
}

.markdown-content .md-link-nav {
  font-weight: 500;
}

.markdown-content .md-link-help {
  font-style: italic;
}
</style>

<style>
.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin: 12px 0 8px 0;
  line-height: 1.3;
  font-weight: 600;
}

.markdown-content h1 {
  font-size: 1.15em;
}

.markdown-content h2 {
  font-size: 1.1em;
}

.markdown-content h3 {
  font-size: 1.0em;
}

.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  font-size: 0.95em;
}

.markdown-content {
  padding-bottom: 8px !important;
}

.markdown-content p {
  margin: 0 0 8px 0;
}

.markdown-content p:last-child {
  margin-bottom: 0 !important;
}

.markdown-content ul:last-child,
.markdown-content ol:last-child {
  margin-bottom: 0 !important;
}

.markdown-content code {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.92em;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1px 4px;
}

.markdown-content pre {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  line-height: 1.4;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  overflow-x: auto;
}

.markdown-content pre code {
  background: transparent;
  border: none;
  padding: 0;
}

.markdown-content table.markdown-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

.markdown-content table.markdown-table th,
.markdown-content table.markdown-table td {
  border: 1px solid #d9d9d9;
  padding: 6px 8px;
  vertical-align: top;
}

.markdown-content table.markdown-table th {
  background: #f7f7f7;
  font-weight: 600;
}

.markdown-content .md-inline-img {
  height: 1em;
  width: auto;
  vertical-align: middle;
  margin: 0 3px;
}
</style>
